import { useTranslation } from 'react-i18next';

import { OPEN_ITEMS_MAX, openItemsOf, type OpenItem } from '@/lib/openItems';

/**
 * Редактор «Ответа по пунктам» (`open_items`, решение владельца 13.10.2026; договор block-types-1.1.2 §8).
 *
 * Пункты (инструкцию над ними рисует запись реестра — RichTextEditor живёт там): текст пункта, подсказка
 * (ученик видит серым под текстом) и образец ответа — его видит только куратор в разборе, ученику сервер
 * его не отдаёт. Правильного ответа нет: проверяет куратор.
 * Предел — OPEN_ITEMS_MAX, его же держит сторож courses (`проверитьПункты`).
 */
export function OpenItemsEditor({
  content,
  onChange,
}: {
  content: Record<string, unknown>;
  onChange: (n: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const items = openItemsOf(content);
  const setItems = (next: OpenItem[]) => onChange({ ...content, items: next });
  const setField = (i: number, field: keyof OpenItem, v: string) =>
    setItems(items.map((it, k) => (k === i ? { ...it, [field]: v } : it)));
  const field = 'w-full rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none';
  const label = 'block text-xs font-medium text-on-surface-variant';

  return (
    <div className="space-y-3">
      <ol className="list-none space-y-3 p-0">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-2.5 w-6 shrink-0 text-right text-sm font-bold text-on-surface">{i + 1}.</span>
            <div className="min-w-0 flex-1 space-y-1.5">
              <input
                type="text"
                value={it.text ?? ''}
                onChange={(e) => setField(i, 'text', e.target.value)}
                placeholder={t('blockRegistry.oiPhText')}
                className={field}
              />
              {/* Подписи над подсказкой и образцом — у заполненного поля плейсхолдера не видно (слово владельца 17.10.2026). */}
              <label className="block space-y-1">
                <span className={label}>{t('blockRegistry.oiLblHint')}</span>
                <input
                  type="text"
                  value={it.hint ?? ''}
                  onChange={(e) => setField(i, 'hint', e.target.value)}
                  placeholder={t('blockRegistry.oiPhHint')}
                  className={field}
                />
              </label>
              <label className="block space-y-1">
                <span className={label}>{t('blockRegistry.oiLblSample')}</span>
                <input
                  type="text"
                  value={it.sample ?? ''}
                  onChange={(e) => setField(i, 'sample', e.target.value)}
                  placeholder={t('blockRegistry.oiPhSample')}
                  className={field}
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => setItems(items.filter((_, k) => k !== i))}
              aria-label={t('blockRegistry.oiDelete')}
              className="mt-1.5 rounded-button p-1 text-on-surface-variant hover:text-error"
            >
              <span className="material-symbols-outlined text-base">delete</span>
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={() => setItems([...items, { text: '', hint: '', sample: '' }])}
        disabled={items.length >= OPEN_ITEMS_MAX}
        className="flex items-center gap-1 text-sm font-medium text-primary disabled:opacity-50"
      >
        <span className="material-symbols-outlined text-base">add</span>
        {t('blockRegistry.oiAdd')}
      </button>
      <p className="text-xs text-on-surface-variant">{t('blockRegistry.oiHint')}</p>
    </div>
  );
}
